import {useEffect, useRef, useState} from 'react';
import type Cropper from 'cropperjs';
import type {StagedFile} from '@securspace/securspace-ui-kit';
import {resolveCropMimeType} from './cropUtils';

export const CROP_FAILED_MESSAGE = 'Crop failed. Please try again.';

export interface UseCropConfirmResult {
  /** True while the async canvas→blob conversion is in flight. */
  confirming: boolean;
  /** User-facing error message, or `null` when there is none to show. */
  error: string | null;
  /** Reads the cropped canvas off `cropper` and converts+emits it via `onComplete`. */
  handleConfirm: (cropper: Cropper | undefined) => void;
}

/**
 * Owns the crop confirm lifecycle: read cropped canvas → normalise MIME type
 * → async `toBlob` conversion → `onComplete` with a new blob URL, or a
 * user-facing error on failure.
 *
 * Guards the async `toBlob` callback against a panel unmount that happens
 * before conversion finishes (e.g. the user dismisses the preview mid-crop),
 * so a stale result is never applied to an already-dismissed file.
 *
 * Does not revoke any blob URLs — that is the caller's responsibility.
 */
export const useCropConfirm = (
  file: StagedFile,
  onComplete: (updated: StagedFile) => void,
  onError?: (message: string) => void
): UseCropConfirmResult => {
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleConfirm = (cropper: Cropper | undefined) => {
    const canvas = cropper?.getCroppedCanvas();
    if (!canvas) {
      // Cropper not ready / image failed to load — surface an error.
      setError(CROP_FAILED_MESSAGE);
      onError?.(CROP_FAILED_MESSAGE);
      return;
    }

    // Normalise MIME type — canvas.toBlob() does not support image/gif.
    const mimeType = resolveCropMimeType(file.file.type);

    setError(null);
    setConfirming(true); // disable confirm button during async conversion

    const fail = () => {
      setConfirming(false);
      setError(CROP_FAILED_MESSAGE);
      onError?.(CROP_FAILED_MESSAGE);
    };

    // A cross-origin photo served without CORS taints the canvas, and toBlob
    // then throws a SecurityError instead of calling back.
    try {
      canvas.toBlob((blob) => {
        if (!isMountedRef.current) {
          // Panel was dismissed while conversion was in flight — discard the
          // result rather than calling setState/onComplete on a stale file.
          return;
        }
        if (!blob) {
          // Conversion failed — re-enable confirm and surface an error.
          fail();
          return;
        }
        setConfirming(false);
        onComplete({...file, previewUrl: URL.createObjectURL(blob)});
      }, mimeType);
    } catch {
      fail();
    }
  };

  return {confirming, error, handleConfirm};
};


